// Customer.js
export class Customer {
  constructor(data = {}) {
    this.id = data.id || this.generateId();
    this.full_name = data.full_name || '';
    this.gender = data.gender || 'female';
    this.phone = data.phone || '';
    this.email = data.email || '';
    this.address = data.address || '';
    this.birth_date = data.birth_date || '';
    this.notes = data.notes || '';
    this.preferred_services = data.preferred_services || [];
    this.skin_type = data.skin_type || '';
    this.allergies = data.allergies || '';
    this.created_at = data.created_at || new Date().toISOString();
    this.updated_at = data.updated_at || new Date().toISOString();
  }

  generateId() {
    return 'customer_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Validation methods
  static validate(data) {
    const errors = [];

    if (!data.full_name || data.full_name.trim().length === 0) {
      errors.push('Full name is required');
    } else {
      // Validate that full name contains at least 2 words
      const nameParts = data.full_name.trim().split(/\s+/);
      if (nameParts.length < 2) {
        errors.push('Full name must contain at least two words (first and last name)');
      }
    }

    if (this.gender && !['female', 'male'].includes(this.gender)) {
      errors.gender = 'Gender must be either female or male';
    }

    if (!data.phone || data.phone.trim().length === 0) {
      errors.push('Phone number is required');
    }

    if (data.email && !this.isValidEmail(data.email)) {
      errors.push('Please enter a valid email address');
    }

    if (data.birth_date && !this.isValidDate(data.birth_date)) {
      errors.push('Please enter a valid birth date');
    }

    if (data.skin_type && !this.isValidSkinType(data.skin_type)) {
      errors.push('Invalid skin type selected');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static isValidDate(dateString) {
    if (!dateString) return true; // Optional field
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;
    
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date) && date <= new Date();
  }

  static isValidSkinType(skinType) {
    const validTypes = ['normal', 'dry', 'oily', 'combination', 'sensitive'];
    return validTypes.includes(skinType);
  }

  // Instance methods
  update(data) {
    Object.keys(data).forEach(key => {
      if (key !== 'id' && key !== 'created_at') {
        this[key] = data[key];
      }
    });
    this.updated_at = new Date().toISOString();
  }

  toJSON() {
    return {
      id: this.id,
      full_name: this.full_name,
      gender: this.gender,
      phone: this.phone,
      email: this.email,
      address: this.address,
      birth_date: this.birth_date,
      notes: this.notes,
      preferred_services: this.preferred_services,
      skin_type: this.skin_type,
      allergies: this.allergies,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  // Static methods for data operations
  static fromJSON(json) {
    return new Customer(json);
  }

  static getSkinTypes() {
    return [
      { value: 'normal', label: 'רגיל' },
      { value: 'dry', label: 'יבש' },
      { value: 'oily', label: 'שמנוני' },
      { value: 'combination', label: 'מעורב' },
      { value: 'sensitive', label: 'רגיש' }
    ];
  }

  // Helper methods for birth date
  getAge() {
    if (!this.birth_date) return null;
    
    const today = new Date();
    const birthDate = new Date(this.birth_date);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  getFormattedBirthDate() {
    if (!this.birth_date) return '';
    
    const date = new Date(this.birth_date);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}